/** Máscaras de entrada (só formatação; a validação continua nos schemas). Todas devolvem o texto já formatado. */
export type MaskKind = 'phone' | 'cpfCnpj' | 'cpf' | 'cnpj' | 'cep' | 'card' | 'expiry' | 'cvv' | 'otp' | 'digits';

export const digitsOf = (raw: string): string => raw.replace(/\D/g, '');

function group(d: string, sizes: number[], seps: string[]): string {
  let out = ''; let i = 0;
  for (let k = 0; k < sizes.length && i < d.length; k++) {
    const part = d.slice(i, i + sizes[k]!);
    out += (k > 0 ? seps[k - 1] : '') + part;
    i += sizes[k]!;
  }
  return out;
}

export function maskPhone(raw: string): string {
  // Internacional fora do Brasil: "+" e dígitos, sem formatação.
  if (raw.trim().startsWith('+') && !raw.trim().startsWith('+55')) return `+${digitsOf(raw).slice(0, 15)}`;
  let d = digitsOf(raw);
  if (d.startsWith('55') && d.length > 11) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
export const maskCpf = (raw: string): string => group(digitsOf(raw).slice(0, 11), [3, 3, 3, 2], ['.', '.', '-']);
export const maskCnpj = (raw: string): string => group(digitsOf(raw).slice(0, 14), [2, 3, 3, 4, 2], ['.', '.', '/', '-']);
export const maskCpfCnpj = (raw: string): string => (digitsOf(raw).length <= 11 ? maskCpf(raw) : maskCnpj(raw));
export const maskCep = (raw: string): string => group(digitsOf(raw).slice(0, 8), [5, 3], ['-']);
export const maskCard = (raw: string): string => group(digitsOf(raw).slice(0, 19), [4, 4, 4, 4, 3], [' ', ' ', ' ', ' ']);
export const maskExpiry = (raw: string): string => group(digitsOf(raw).slice(0, 4), [2, 2], ['/']);
export const maskCvv = (raw: string): string => digitsOf(raw).slice(0, 4);
export const maskOtp = (raw: string): string => digitsOf(raw).slice(0, 6);

export function applyMask(kind: MaskKind, raw: string): string {
  switch (kind) {
    case 'phone': return maskPhone(raw);
    case 'cpfCnpj': return maskCpfCnpj(raw);
    case 'cpf': return maskCpf(raw);
    case 'cnpj': return maskCnpj(raw);
    case 'cep': return maskCep(raw);
    case 'card': return maskCard(raw);
    case 'expiry': return maskExpiry(raw);
    case 'cvv': return maskCvv(raw);
    case 'otp': return maskOtp(raw);
    case 'digits': return digitsOf(raw);
  }
}

/** Atributos de teclado/autocomplete adequados a cada máscara (celular abre o teclado certo). */
export function maskAttrs(kind: MaskKind): { inputMode: 'numeric' | 'tel'; autoComplete?: string; placeholder?: string; maxLength?: number } {
  switch (kind) {
    case 'phone': return { inputMode: 'tel', autoComplete: 'tel', placeholder: '(11) 99999-9999', maxLength: 16 };
    case 'cpfCnpj': return { inputMode: 'numeric', placeholder: '000.000.000-00', maxLength: 18 };
    case 'cpf': return { inputMode: 'numeric', placeholder: '000.000.000-00', maxLength: 14 };
    case 'cnpj': return { inputMode: 'numeric', placeholder: '00.000.000/0000-00', maxLength: 18 };
    case 'cep': return { inputMode: 'numeric', autoComplete: 'postal-code', placeholder: '00000-000', maxLength: 9 };
    case 'card': return { inputMode: 'numeric', autoComplete: 'cc-number', placeholder: '0000 0000 0000 0000', maxLength: 23 };
    case 'expiry': return { inputMode: 'numeric', autoComplete: 'cc-exp', placeholder: 'MM/AA', maxLength: 5 };
    case 'cvv': return { inputMode: 'numeric', autoComplete: 'cc-csc', placeholder: '123', maxLength: 4 };
    case 'otp': return { inputMode: 'numeric', autoComplete: 'one-time-code', placeholder: '000000', maxLength: 6 };
    case 'digits': return { inputMode: 'numeric' };
  }
}

/** Número → texto no idioma (pt-BR: 1.234,56). */
export function formatNumber(v: number, locale: string, decimals = 0, maxDecimals = decimals): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: Math.max(decimals, maxDecimals) }).format(v);
}
/** Texto digitado (com vírgula ou ponto) → número; vazio/inválido → null. */
export function parseNumber(text: string, locale: string): number | null {
  const t = text.trim();
  if (!t) return null;
  const decSep = new Intl.NumberFormat(locale).format(1.5).replace(/\d/g, '');
  const cleaned = decSep === ',' ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  const n = Number(cleaned.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}
